import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash-es';
import { ChannelSort } from 'stream-chat';
import { ChannelSearchFunctionParams, useChatContext } from 'stream-chat-react';
import type { ChannelFilters as StreamChannelFilters } from 'stream-chat';
import { resolveMessageSearchResults } from '../ChatContent/utils';

const PAGE_SIZE = 10;

type UseChannelSearchParams = {
  filters: StreamChannelFilters;
  sortOption: ChannelSort;
  onSearch?: (query: string) => void;
};

type ActiveSearch = {
  query: string;
  params: ChannelSearchFunctionParams;
  limit: number;
};

export const useChannelSearch = ({ filters, sortOption, onSearch }: UseChannelSearchParams) => {
  const { client } = useChatContext();
  const [activeSearch, setActiveSearch] = useState<ActiveSearch | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const runSearch = useMemo(
    () => async (q: string, params: ChannelSearchFunctionParams, limit: number) => {
      const { setResults, setSearching } = params;
      const options = { limit };
      const [channelsResp, messagesRes] = await Promise.allSettled([
        client.queryChannels({ ...filters, name: { $autocomplete: q } }, sortOption, options),
        client.search(filters, q, options),
      ]);

      const channels = channelsResp.status === 'fulfilled' ? channelsResp.value : [];
      const messageResults = messagesRes.status === 'fulfilled' ? messagesRes.value.results : [];
      const msgChannels = await resolveMessageSearchResults(messageResults, client);

      setResults([...channels, ...msgChannels]);
      setSearching(false);
      setIsLoadingMore(false);
      setHasMore(channels.length >= limit || messageResults.length >= limit);
    },
    [client, filters, sortOption],
  );

  const doSearch = useMemo(() => debounce(runSearch, 400), [runSearch]);

  useEffect(() => {
    if (!activeSearch) return;

    activeSearch.params.setSearching(true);
    doSearch(activeSearch.query, activeSearch.params, activeSearch.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const searchFunction = (params: ChannelSearchFunctionParams, e: React.BaseSyntheticEvent) => {
    const { setResults, setSearching, setQuery } = params;
    const q = e.target.value;

    setQuery(q);
    onSearch?.(q);

    if (!q) {
      doSearch.cancel();
      setActiveSearch(null);
      setHasMore(false);
      setResults([]);
      return;
    }

    setActiveSearch({ query: q, params, limit: PAGE_SIZE });
    setSearching(true);
    doSearch(q, params, PAGE_SIZE);
  };

  const loadMore = () => {
    if (!activeSearch || isLoadingMore) return;

    const limit = activeSearch.limit + PAGE_SIZE;
    setActiveSearch({ ...activeSearch, limit });
    setIsLoadingMore(true);
    runSearch(activeSearch.query, activeSearch.params, limit);
  };

  return { searchFunction, hasMore, isLoadingMore, loadMore };
};
